import { AppError, type RestResponse } from "@artisancode/types";
import { Mail, MessageCircle, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { IconOptionGrid } from "@/components/shared/icon-option-grid";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAddWhatsappDevice } from "@/hooks/use-whatsapp";
import { EmailConnectionFields } from "@/pages/settings/email/email-connection-fields";
import { useEmailConnectionForm } from "@/pages/settings/email/use-email-connection-form";
import { ResendConnectionFields } from "@/pages/settings/resend/resend-connection-fields";
import { useResendConnectionForm } from "@/pages/settings/resend/use-resend-connection-form";

function errorMessage(error: unknown, fallback: string) {
  if (error instanceof AppError && (error.httpCode ?? 0) < 500) {
    const backendMessage = (error.data as RestResponse | undefined)?.message;
    return backendMessage || error.message;
  }
  return fallback;
}

const providerOptions = [
  { value: "whatsapp" as const, label: "WhatsApp", icon: MessageCircle },
  { value: "smtp" as const, label: "Email (SMTP)", icon: Mail },
  { value: "resend" as const, label: "Resend", icon: Send },
];

export function AddConnectionDialog({
  open,
  onOpenChange,
  onWhatsappAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onWhatsappAdded: (deviceId: string) => void;
}) {
  const [provider, setProvider] = useState<"whatsapp" | "smtp" | "resend">(
    "whatsapp",
  );
  const [whatsappName, setWhatsappName] = useState("");

  const addDevice = useAddWhatsappDevice();
  const emailForm = useEmailConnectionForm(
    open && provider === "smtp",
    null,
    () => onOpenChange(false),
  );
  const resendForm = useResendConnectionForm(
    open && provider === "resend",
    null,
    () => onOpenChange(false),
  );

  async function handleAddWhatsapp() {
    try {
      const device = await addDevice.mutateAsync(whatsappName.trim());
      onOpenChange(false);
      setWhatsappName("");
      onWhatsappAdded(device.id);
    } catch (error) {
      toast.error(errorMessage(error, "Gagal menambah device WhatsApp."));
    }
  }

  const isSubmitting =
    provider === "whatsapp"
      ? addDevice.isPending
      : provider === "smtp"
        ? emailForm.isSubmitting
        : resendForm.isSubmitting;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setProvider("whatsapp");
          setWhatsappName("");
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Tambah Koneksi</DialogTitle>
          <DialogDescription>
            Pilih provider pengirim notifikasi.
          </DialogDescription>
        </DialogHeader>

        <IconOptionGrid
          options={providerOptions}
          value={provider}
          onChange={setProvider}
        />

        {provider === "whatsapp" && (
          <div className="grid gap-3">
            <div className="grid gap-2">
              <Label htmlFor="whatsapp-name">Nama Koneksi</Label>
              <Input
                id="whatsapp-name"
                placeholder="misal: WA Admin Toko"
                autoFocus
                value={whatsappName}
                onChange={(e) => setWhatsappName(e.target.value)}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Device baru akan dibuat dan QR code langsung ditampilkan untuk
              dipindai.
            </p>
          </div>
        )}
        {provider === "smtp" && (
          <Form {...emailForm.form}>
            <form id="add-smtp-form" onSubmit={emailForm.onSubmit}>
              <EmailConnectionFields form={emailForm.form} editing={null} />
            </form>
          </Form>
        )}
        {provider === "resend" && (
          <Form {...resendForm.form}>
            <form id="add-resend-form" onSubmit={resendForm.onSubmit}>
              <ResendConnectionFields form={resendForm.form} editing={null} />
            </form>
          </Form>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Batal
          </Button>
          {provider === "whatsapp" && (
            <Button
              type="button"
              onClick={handleAddWhatsapp}
              disabled={isSubmitting || !whatsappName.trim()}
            >
              {isSubmitting ? "Menambah..." : "Tambah"}
            </Button>
          )}
          {provider === "smtp" && (
            <Button type="submit" form="add-smtp-form" disabled={isSubmitting}>
              {isSubmitting ? "Menyimpan..." : "Tambah"}
            </Button>
          )}
          {provider === "resend" && (
            <Button
              type="submit"
              form="add-resend-form"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Menyimpan..." : "Tambah"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
